import {
  activeKeyQueryKeys,
  activityTabKeys,
  activityTabQueryKeys,
  shareBySheetKeys,
} from '@/features/project/_utils/search-query-states';
import type { PinpointMappingType } from '../models/pinpoint';
import {
  KsInvestmentBasicInformationScenePinpointFieldEnum,
  KsInvestmentFundBusinessScenePinpointFieldEnum,
  KsInvestmentFundInvestmentsScenePinpointFieldEnum,
  KsInvestmentRelatedMaterialScenePinpointFieldEnum,
} from './ks-investment-pinpoint';
import {
  KsLocalGovernmentActivitiesScenePinpointFieldEnum,
  type KsLocalGovernmentActivity,
  type KsLocalGovernmentActivityFlowPinpointMappingType,
  KsLocalGovernmentActivityFlowScenePinpointFieldEnum,
  KsLocalGovernmentBasicInformationScenePinpointFieldEnum,
  KsLocalGovernmentExecutionProgressScenePinpointFieldEnum,
  KsLocalGovernmentFundAdditionalAttributesScenePinpointFieldEnum,
  KsLocalGovernmentRelatedMaterialScenePinpointFieldEnum,
} from './ks-local-government-pinpoint';
import {
  KsStandardActivitiesScenePinpointFieldEnum,
  type KsStandardActivity,
  type KsStandardActivityFlowPinpointMappingType,
  KsStandardActivityFlowScenePinpointFieldEnum,
  KsStandardBasicInformationScenePinpointFieldEnum,
  KsStandardFundAdditionalAttributesScenePinpointFieldEnum,
  KsStandardFundBusinessScenePinpointFieldEnum,
  KsStandardIncomeAndExpenditureScenePinpointFieldEnum,
  KsStandardInspectionEvaluationScenePinpointFieldEnum,
  KsStandardOwnershipRatioScenePinpointFieldEnum,
  KsStandardPaymentScenePinpointFieldEnum,
  KsStandardRelatedMaterialScenePinpointFieldEnum,
} from './ks-standard-pinpoint';
import {
  type RsActivitiesAndOutcomesTitle,
  RsActivitiesScenePinpointFieldEnum,
  type RsActivityFlowPinpointMappingType,
  RsActivityFlowScenePinpointFieldEnum,
  RsBasicInformationScenePinpointFieldEnum,
  RsDetailedBreakdownScenePinpointFieldEnum,
  RsInspectionEvaluationScenePinpointFieldEnum,
  RsPaymentScenePinpointFieldEnum,
  RsRelatedMaterialScenePinpointFieldEnum,
} from './rs-pinpoint';
import {
  RsSkippedReviewBasicInformationScenePinpointFieldEnum,
  RsSkippedReviewDetailedBreakdownScenePinpointFieldEnum,
  RsSkippedReviewRelatedMaterialScenePinpointFieldEnum,
} from './rs-skipped-review-pinpoint';
import {
  SsActivitiesScenePinpointFieldEnum,
  type SsActivity,
  type SsActivityFlowPinpointMappingType,
  SsActivityFlowScenePinpointFieldEnum,
  SsBasicInformationScenePinpointFieldEnum,
  SsDetailedBreakdownScenePinpointFieldEnum,
  SsInspectionEvaluationScenePinpointFieldEnum,
  SsPaymentScenePinpointFieldEnum,
  SsProjectRelatedMaterialScenePinpointFieldEnum,
} from './ss-pinpoint';

const activeKey = shareBySheetKeys.Enum.activeKey;
const activeKeyQueryKey = activeKeyQueryKeys.Enum;

type ActivityFlowMappingType =
  | RsActivityFlowPinpointMappingType
  | RsActivitiesAndOutcomesTitle
  | SsActivityFlowPinpointMappingType
  | KsLocalGovernmentActivityFlowPinpointMappingType
  | KsStandardActivityFlowPinpointMappingType
  | KsLocalGovernmentActivity
  | KsStandardActivity
  | SsActivity;

type TabMappingType =
  | {
      [activeKey]: string;
    }
  | ((pinpoint: ActivityFlowMappingType) => {
      [activeKey]: string;
      [activityTabQueryKeys.Enum.activitiesTabKey]?: string;
      [activityTabQueryKeys.Enum.routeIndicatorIds]?: string | undefined;
    });

// フィールドとタブのマッピングを定義
const FIELD_TO_TAB_MAPPING: Record<string, TabMappingType> = {
  // Basic Information fields
  ...Object.fromEntries(
    [
      ...RsBasicInformationScenePinpointFieldEnum.options,
      ...RsSkippedReviewBasicInformationScenePinpointFieldEnum.options,
      ...SsBasicInformationScenePinpointFieldEnum.options,
      ...KsInvestmentBasicInformationScenePinpointFieldEnum.options,
      ...KsLocalGovernmentBasicInformationScenePinpointFieldEnum.options,
      ...KsStandardBasicInformationScenePinpointFieldEnum.options,
    ].map((field) => {
      return [field, { [activeKey]: activeKeyQueryKey['basic-information'] }];
    }),
  ),

  // Detailed Breakdown fields
  ...Object.fromEntries(
    [
      ...RsDetailedBreakdownScenePinpointFieldEnum.options,
      ...RsSkippedReviewDetailedBreakdownScenePinpointFieldEnum.options,
      ...SsDetailedBreakdownScenePinpointFieldEnum.options,
    ].map((field) => {
      return [
        field,
        {
          [activeKey]: activeKeyQueryKey['detailed-breakdown'],
        },
      ];
    }),
  ),

  // Activity Flow fields
  ...Object.fromEntries(
    [
      ...RsActivityFlowScenePinpointFieldEnum.options,
      ...SsActivityFlowScenePinpointFieldEnum.options,
      ...KsLocalGovernmentActivityFlowScenePinpointFieldEnum.options,
      ...KsStandardActivityFlowScenePinpointFieldEnum.options,
    ].map((field) => [
      field,
      (pinpoint: ActivityFlowMappingType) => {
        if (pinpoint.field === 'rs.activities-and-outcomes-title') {
          return {
            [activeKey]: activeKeyQueryKey.activities,
          };
        }
        return {
          [activeKey]: activeKeyQueryKey.activities,
          [activityTabQueryKeys.Enum.activitiesTabKey]:
            activityTabKeys.Enum['activity-flow'],
          [activityTabQueryKeys.Enum.routeIndicatorIds]:
            pinpoint.path.routeIndicatorId,
        };
      },
    ]),
  ),

  // Activities fields
  ...Object.fromEntries(
    [
      ...RsActivitiesScenePinpointFieldEnum.options,
      ...SsActivitiesScenePinpointFieldEnum.options,
      ...KsLocalGovernmentActivitiesScenePinpointFieldEnum.options,
      ...KsStandardActivitiesScenePinpointFieldEnum.options,
    ].map((field) => {
      return [
        field,
        (pinpoint: ActivityFlowMappingType) => {
          if (
            pinpoint.field === 'ks.local-government.activity' ||
            pinpoint.field === 'ks.standard.activity' ||
            pinpoint.field === 'ss.activity'
          ) {
            return {
              [activeKey]: activeKeyQueryKey.activities,
              [activityTabQueryKeys.Enum.activitiesTabKey]:
                activityTabKeys.Enum['activity-flow'],
              [activityTabQueryKeys.Enum.routeIndicatorIds]:
                pinpoint.path.routeIndicatorId,
            };
          }

          return {
            [activeKey]: activeKeyQueryKey.activities,
            [activityTabQueryKeys.Enum.activitiesTabKey]:
              activityTabKeys.Enum.activities,
          };
        },
      ];
    }),
  ),

  // Inspection Evaluation fields
  ...Object.fromEntries(
    [
      ...RsInspectionEvaluationScenePinpointFieldEnum.options,
      ...SsInspectionEvaluationScenePinpointFieldEnum.options,
      ...KsStandardInspectionEvaluationScenePinpointFieldEnum.options,
    ].map((field) => [
      field,
      { [activeKey]: activeKeyQueryKey['inspection-evaluation'] },
    ]),
  ),

  // Payment fields
  ...Object.fromEntries(
    [
      ...RsPaymentScenePinpointFieldEnum.options,
      ...SsPaymentScenePinpointFieldEnum.options,
      ...KsStandardPaymentScenePinpointFieldEnum.options,
    ].map((field) => [field, { [activeKey]: activeKeyQueryKey.payment }]),
  ),

  // Related Material fields
  ...Object.fromEntries(
    [
      ...RsRelatedMaterialScenePinpointFieldEnum.options,
      ...RsSkippedReviewRelatedMaterialScenePinpointFieldEnum.options,
      ...SsProjectRelatedMaterialScenePinpointFieldEnum.options,
      ...KsInvestmentRelatedMaterialScenePinpointFieldEnum.options,
      ...KsLocalGovernmentRelatedMaterialScenePinpointFieldEnum.options,
      ...KsStandardRelatedMaterialScenePinpointFieldEnum.options,
    ].map((field) => [
      field,
      { [activeKey]: activeKeyQueryKey['related-material'] },
    ]),
  ),

  // Fund Investments fields
  ...Object.fromEntries(
    [...KsInvestmentFundInvestmentsScenePinpointFieldEnum.options].map(
      (field) => [
        field,
        { [activeKey]: activeKeyQueryKey['fund-investments'] },
      ],
    ),
  ),

  // Fund Business fields
  ...Object.fromEntries(
    [
      ...KsInvestmentFundBusinessScenePinpointFieldEnum.options,
      ...KsStandardFundBusinessScenePinpointFieldEnum.options,
    ].map((field) => [
      field,
      { [activeKey]: activeKeyQueryKey['fund-business'] },
    ]),
  ),

  // Fund Additional Attributes fields
  ...Object.fromEntries(
    [
      ...KsLocalGovernmentFundAdditionalAttributesScenePinpointFieldEnum.options,
      ...KsStandardFundAdditionalAttributesScenePinpointFieldEnum.options,
    ].map((field) => [
      field,
      { [activeKey]: activeKeyQueryKey['fund-additional-attributes'] },
    ]),
  ),

  // Execution Progress fields
  ...Object.fromEntries(
    [...KsLocalGovernmentExecutionProgressScenePinpointFieldEnum.options].map(
      (field) => [
        field,
        { [activeKey]: activeKeyQueryKey['execution-progress'] },
      ],
    ),
  ),

  // Ownership Ratio fields
  ...Object.fromEntries(
    [...KsStandardOwnershipRatioScenePinpointFieldEnum.options].map((field) => [
      field,
      { [activeKey]: activeKeyQueryKey['ownership-ratio'] },
    ]),
  ),

  // Income and Expenditure fields
  ...Object.fromEntries(
    [...KsStandardIncomeAndExpenditureScenePinpointFieldEnum.options].map(
      (field) => [
        field,
        { [activeKey]: activeKeyQueryKey['income-and-expenditure'] },
      ],
    ),
  ),
};

export type ReturnQueryParamsType = {
  [activeKey]: string;
  [activityTabQueryKeys.Enum.activitiesTabKey]?: string;
  [activityTabQueryKeys.Enum.routeIndicatorIds]?: string[];
};

// フィールドに応じてタブのクエリパラメータを返却
export const getQueryParamsByField = (
  pinpoint: PinpointMappingType | undefined,
): ReturnQueryParamsType | undefined => {
  if (!pinpoint) {
    return undefined;
  }
  const queryParams: TabMappingType | undefined =
    FIELD_TO_TAB_MAPPING[pinpoint.field];

  if (!queryParams) {
    return undefined;
  }
  const result =
    typeof queryParams === 'function'
      ? queryParams(pinpoint as ActivityFlowMappingType)
      : queryParams;

  return result;
};
